import { useEffect, useRef, useState } from "react";
import { motion, animate, useReducedMotion } from "framer-motion";
import { Check, TrendingUp } from "lucide-react";

const CYAN = "#00C8C8";

// Escenarios reales de cálculo SAC que se van alternando ("calculadora viva")
const ESCENARIOS = [
  { mejor: 850000, dias: 181, prop: 1.0, bruto: 425000, neto: 350625 },
  { mejor: 1240000, dias: 90, prop: 0.497, bruto: 308140, neto: 254215 },
  { mejor: 640000, dias: 150, prop: 0.829, bruto: 265280, neto: 218856 },
  { mejor: 1580000, dias: 120, prop: 0.663, bruto: 523770, neto: 432110 },
];

const arMoney = (v) => "$ " + Math.round(v).toLocaleString("es-AR");
const arInt = (v) => Math.round(v).toLocaleString("es-AR");

function useCountUp(target, duration = 1.2) {
  const [val, setVal] = useState(target);
  const ref = useRef(target);
  useEffect(() => {
    const controls = animate(ref.current, target, {
      duration,
      ease: "easeOut",
      onUpdate: (v) => { ref.current = v; setVal(v); },
    });
    return () => controls.stop();
  }, [target, duration]);
  return val;
}

// Motivos de marca flotantes (puntos, ×, +, triángulos, anillos)
const MOTIFS = [
  { t: "ring", x: "6%", y: "12%", s: 26, d: 0 },
  { t: "plus", x: "88%", y: "8%", s: 18, d: 0.6 },
  { t: "dot", x: "92%", y: "46%", s: 9, d: 1.1 },
  { t: "cross", x: "4%", y: "62%", s: 16, d: 0.3 },
  { t: "tri", x: "84%", y: "82%", s: 22, d: 0.9 },
  { t: "dot", x: "14%", y: "90%", s: 7, d: 1.4 },
  { t: "ring", x: "70%", y: "94%", s: 18, d: 0.5 },
  { t: "plus", x: "50%", y: "4%", s: 14, d: 1.2 },
];

const Motif = ({ t, s }) => {
  if (t === "dot") return <div style={{ width: s, height: s, background: CYAN, borderRadius: 999, opacity: 0.8 }} />;
  if (t === "ring") return <div style={{ width: s, height: s, border: `2px solid ${CYAN}`, borderRadius: 999, opacity: 0.5 }} />;
  if (t === "plus") return <span style={{ color: CYAN, fontSize: s, lineHeight: 1, fontWeight: 300, opacity: 0.6 }}>+</span>;
  if (t === "cross") return <span style={{ color: "#fff", fontSize: s, lineHeight: 1, fontWeight: 200, opacity: 0.4 }}>×</span>;
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" style={{ opacity: 0.45 }}>
      <polygon points="12,3 22,21 2,21" fill="none" stroke={CYAN} strokeWidth="1.6" />
    </svg>
  );
};

export default function HeroCalculadora() {
  const reduce = useReducedMotion();
  const [i, setI] = useState(0);
  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => setI((p) => (p + 1) % ESCENARIOS.length), 3600);
    return () => clearInterval(id);
  }, [reduce]);

  const e = ESCENARIOS[i];
  const mejor = useCountUp(e.mejor);
  const dias = useCountUp(e.dias, 1);
  const bruto = useCountUp(e.bruto);
  const neto = useCountUp(e.neto);

  const R = 34, C = 2 * Math.PI * R;

  return (
    <div className="relative w-full" data-testid="hero-animation">
      {/* Motivos de marca flotantes */}
      {!reduce && MOTIFS.map((m, idx) => (
        <motion.div key={idx} className="absolute z-0 pointer-events-none" style={{ left: m.x, top: m.y }}
          initial={{ y: 0, opacity: 0 }}
          animate={{ y: [-6, 6, -6], opacity: [0.5, 1, 0.5], rotate: m.t === "tri" || m.t === "plus" ? [0, 25, 0] : 0 }}
          transition={{ duration: 5 + m.d * 2, repeat: Infinity, ease: "easeInOut", delay: m.d }}>
          <Motif t={m.t} s={m.s} />
        </motion.div>
      ))}

      {/* Tarjeta de cálculo (glass) */}
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className="relative z-10 mx-auto max-w-md rounded-2xl p-6 sm:p-7"
        style={{
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.14)",
          backdropFilter: "blur(16px)",
          boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6)",
        }}>
        {/* header tarjeta */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              {!reduce && <span className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping" style={{ background: CYAN }} />}
              <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: CYAN }} />
            </span>
            <span className="text-[0.62rem] font-semibold tracking-[0.18em] uppercase" style={{ color: CYAN }}>Cálculo en vivo</span>
          </div>
          <span className="text-[0.62rem] tracking-[0.14em] uppercase text-white/45">SAC · CCT 130/75</span>
        </div>

        <div className="flex items-center gap-5">
          {/* anillo proporción */}
          <div className="relative shrink-0" style={{ width: 92, height: 92 }}>
            <svg width="92" height="92" viewBox="0 0 92 92" className="-rotate-90">
              <circle cx="46" cy="46" r={R} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="7" />
              <motion.circle cx="46" cy="46" r={R} fill="none" stroke={CYAN} strokeWidth="7" strokeLinecap="round"
                strokeDasharray={C} animate={{ strokeDashoffset: C * (1 - e.prop) }}
                transition={{ duration: 1.1, ease: "easeOut" }} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-head font-extrabold text-white text-lg leading-none">{Math.round(e.prop * 100)}%</span>
              <span className="text-[0.5rem] uppercase tracking-wider text-white/50 mt-0.5">Proporción</span>
            </div>
          </div>

          {/* filas */}
          <div className="flex-1 space-y-2.5 min-w-0">
            <Row label="Mejor remuneración" value={arMoney(mejor)} />
            <Row label="Días trabajados" value={arInt(dias)} />
            <Row label="Aguinaldo bruto" value={arMoney(bruto)} />
          </div>
        </div>

        {/* resultado neto */}
        <div className="mt-5 rounded-xl px-4 py-3.5 flex items-center justify-between" style={{ background: "rgba(0,200,200,0.12)", border: `1px solid ${CYAN}55` }}>
          <div>
            <p className="text-[0.6rem] uppercase tracking-[0.16em] text-white/55">Aguinaldo neto</p>
            <motion.p key={i} initial={{ opacity: 0.4 }} animate={{ opacity: 1 }} className="font-head font-extrabold text-2xl text-white tabular-nums leading-tight">
              {arMoney(neto)}
            </motion.p>
          </div>
          <div className="flex items-center gap-1.5 rounded-full px-2.5 py-1.5" style={{ background: CYAN }}>
            <Check size={13} strokeWidth={3} className="text-[#042256]" />
            <span className="text-[0.62rem] font-bold text-[#042256]">Recibo listo</span>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-1.5 text-white/40 text-[0.65rem]">
          <TrendingUp size={12} />
          <span>Topes, detracción Dto. 814 y OSECAC aplicados automáticamente</span>
        </div>
      </motion.div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3 border-b border-white/10 pb-1.5">
    <span className="text-xs text-white/55 truncate">{label}</span>
    <span className="font-mono text-sm text-white tabular-nums whitespace-nowrap">{value}</span>
  </div>
);
